import React, { useState, useCallback, useRef } from 'react'
import { Head } from '@inertiajs/react'
import { router } from '@inertiajs/react'
import { PublicLayout } from '@/components/frontend/templates/PublicLayout'
import { Section } from '@/components/frontend/atoms/layout/Section'
import { Container } from '@/components/frontend/atoms/layout/Container'
import { Stack } from '@/components/shared/atoms/layout/Stack'
import { SectionIntro } from '@/components/frontend/molecules/SectionIntro'
import { SearchBox } from '@/components/frontend/atoms/form/SearchBox'
import { PostFeed } from '@/components/frontend/organisms/PostFeed'
import { TagFilter } from '@/components/frontend/molecules/TagFilter'
import { useTranslation } from '@/hooks/useTranslation'
import type { PageProps } from '@/types'
import type { PublicPost, BlogPagination } from '@/types/frontend'

interface TagItem {
  id: number
  name: string
  slug: string
}

type Props = PageProps<{
  posts: PublicPost[]
  pagination: BlogPagination
  tags: TagItem[]
  activeTag: string | null
  searchQuery: string
}>

const DEBOUNCE_MS = 400

const BlogIndex: React.FC<Props> = ({ posts, pagination, tags, activeTag, searchQuery }) => {
  const { t, lp } = useTranslation()
  const [search, setSearch] = useState(searchQuery || '')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const navigateWithSearch = useCallback((value: string) => {
    const params: Record<string, string> = {}
    const trimmed = value.trim()
    if (trimmed) params.search = trimmed
    if (activeTag) params.tag = activeTag

    router.get(lp('/blog'), params, { preserveState: true, preserveScroll: true })
  }, [activeTag, lp])

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => navigateWithSearch(value), DEBOUNCE_MS)
  }, [navigateWithSearch])

  return (
    <PublicLayout>
      <Head title={t('head.blog_title')} />
      <Section padding="md">
        <Container>
          <Stack gap="12">
            <Stack gap="8">
              <SectionIntro label={t('blog.label')} title={t('blog.title')} subtitle={t('blog.subtitle')} as="h1" />
              <SearchBox value={search} onChange={handleSearchChange} placeholder={t('blog.search_placeholder')} />
              <TagFilter tags={tags} activeTag={activeTag} />
            </Stack>
            <PostFeed posts={posts} pagination={pagination} activeTag={activeTag} search={search.trim()} />
          </Stack>
        </Container>
      </Section>
    </PublicLayout>
  )
}

export default BlogIndex
